/**
 * Google Analytics 4 (GA4) integration utilities.
 *
 * - Page view tracking via React Router
 * - Custom conversion events (book_now_click, begin_checkout, purchase)
 *
 * Analytics is disabled by default until auth resolves. Events fired before
 * auth completes are buffered and flushed (or discarded) once the user's
 * role is known. This prevents admin/host activity from polluting funnel data.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

// Extend window with gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

const GA_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;

// ─── Auth-aware gating ───────────────────────────────────────

type AnalyticsState = 'pending' | 'enabled' | 'disabled';

let state: AnalyticsState = 'pending';
let eventBuffer: Array<() => void> = [];

/**
 * Called once auth resolves. Flushes buffered events if enabled,
 * discards them if the user is admin/host.
 */
export function setAnalyticsEnabled(enabled: boolean) {
  state = enabled ? 'enabled' : 'disabled';
  if (state === 'enabled') {
    eventBuffer.forEach((fn) => fn());
  }
  eventBuffer = [];
}

function sendOrBuffer(fn: () => void) {
  if (!GA_ID || !window.gtag) return;
  if (state === 'enabled') {
    fn();
  } else if (state === 'pending') {
    eventBuffer.push(fn);
  }
  // state === 'disabled' → drop silently
}

// ─── Page Views ──────────────────────────────────────────────

// Paths excluded from analytics tracking (admin/host dashboards)
const EXCLUDED_PATH_PREFIXES = ['/admin', '/host'];

// Query params that authorize an action rather than describe a page. Sending
// one to GA4 would hand a working credential to a third party, so they are
// redacted from page_path. The unfollow link's token is one of them.
const SENSITIVE_QUERY_PARAMS = ['token'];

/** Replace the value of any credential-bearing query param with "redacted". */
export function redactSensitiveParams(path: string): string {
  const queryStart = path.indexOf('?');
  if (queryStart === -1) return path;

  const params = new URLSearchParams(path.slice(queryStart + 1));
  let changed = false;
  for (const name of SENSITIVE_QUERY_PARAMS) {
    if (params.has(name)) {
      params.set(name, 'redacted');
      changed = true;
    }
  }
  if (!changed) return path;

  const query = params.toString();
  return query ? `${path.slice(0, queryStart)}?${query}` : path.slice(0, queryStart);
}

/**
 * Send a page_view event. Called on every route change.
 * Excludes admin and host dashboard pages.
 *
 * page_location is passed explicitly: gtag defaults it to document.location,
 * which would ship the raw query string, credentials and all, no matter how
 * carefully page_path was redacted.
 */
export function trackPageView(path: string) {
  if (EXCLUDED_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))) return;
  const safePath = redactSensitiveParams(path);
  const safeLocation = `${window.location.origin}${safePath}`;
  sendOrBuffer(() =>
    window.gtag!('event', 'page_view', { page_path: safePath, page_location: safeLocation })
  );
}

// ─── Conversion Events ──────────────────────────────────────

/**
 * User clicked "Book Now" on the activity detail page.
 */
export function trackBookNowClick(activityTitle: string) {
  sendOrBuffer(() => window.gtag!('event', 'book_now_click', { activity_title: activityTitle }));
}

/**
 * User entered the checkout page (booking or pass).
 */
export function trackBeginCheckout(params: {
  type: 'booking' | 'pass' | 'course';
  title: string;
  value?: number; // dollar amount
}) {
  sendOrBuffer(() =>
    window.gtag!('event', 'begin_checkout', {
      checkout_type: params.type,
      item_name: params.title,
      value: params.value,
      currency: 'USD',
    })
  );
}

/**
 * User completed a purchase (redirected to success page).
 */
export function trackPurchase(params: {
  type: 'booking' | 'pass' | 'course';
  transactionId?: string;
  value?: number; // dollar amount
  title?: string;
}) {
  sendOrBuffer(() =>
    window.gtag!('event', 'purchase', {
      transaction_id: params.transactionId,
      value: params.value,
      currency: 'USD',
      purchase_type: params.type,
      item_name: params.title,
    })
  );
}
